import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Camera, Dumbbell, Check, ArrowRight, Sparkles, Video, Award,
  Flame, Trophy, Zap, ShieldCheck, X, Crown,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { useSoloAnnualPromo } from '../hooks/useSoloAnnualPromo';
import { SoloAnnualPromoCard } from '../components/shared';
import { COACHING_TIERS, COACHING_COMPARE, OfferCard } from './OffresPage';

const IMG = '/images/app-showcase';
const MEDIA = process.env.REACT_APP_BACKEND_URL;

const SORA_VIDEOS = [
  { group_fr: 'Pectoraux', group_en: 'Chest', url: '/api/exercise-media/body-up/exercises-v2-video/7f57e93a1b9d-developpe_couche_a_la_barre_libre-00ac2eb8.mp4' },
  { group_fr: 'Dos', group_en: 'Back', url: '/api/exercise-media/body-up/exercises-v2-video/ceef501e355f-tirage_vertical_a_la_machine__assisted_lat_pulldown-1e2af7d2.mp4' },
  { group_fr: 'Épaules', group_en: 'Shoulders', url: '/api/exercise-media/body-up/exercises-v2-video/bf8188ff0829-developpe_militaire_a_la_machine_convergente-f7020a72.mp4' },
  { group_fr: 'Biceps', group_en: 'Biceps', url: '/api/exercise-media/body-up/exercises-v2-video/a95369515e9b-curl_a_la_machine__biceps_curl_assis-634a5953.mp4' },
  { group_fr: 'Abdominaux', group_en: 'Abs', url: '/api/exercise-media/body-up/exercises-v2-video/a5534b582a31-ab_wheel_roll_out-1d6059aa.mp4' },
  { group_fr: 'Fessiers', group_en: 'Glutes', url: '/api/exercise-media/body-up/exercises-v2-video/a494a37f9603-bulgarian_split_squat_a_la_barre-60ed8fc1.mp4' },
  { group_fr: 'Cardio', group_en: 'Cardio', url: '/api/exercise-media/body-up/exercises-v2-video/568006f281f7-bear_crawl-041a36a1.mp4' },
  { group_fr: 'Trapèzes', group_en: 'Traps', url: '/api/exercise-media/body-up/exercises-v2-video/2e7a4938e5b6-shrugs_a_la_machine_convergente-935d3302.mp4' },
];

/* ---------------- Petits composants ---------------- */

const Eyebrow = ({ children }) => (
  <p className="text-primary text-xs font-bold uppercase tracking-[0.2em] mb-3">{children}</p>
);

const Shot = ({ src, alt, reverse }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, rotateY: reverse ? 4 : -4 }}
    whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    whileHover={{ rotateY: reverse ? -3 : 3, rotateX: -2, scale: 1.015 }}
    transition={{ duration: 0.6, ease: 'easeOut' }}
    className={`relative group ${reverse ? 'lg:order-1' : 'lg:order-2'}`}
    style={{ transformStyle: 'preserve-3d' }}
  >
    <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-primary/25 via-fuchsia-500/15 to-purple-600/20 blur-2xl opacity-70 -z-10" aria-hidden="true" />
    <div className="rounded-[1.5rem] border border-white/10 bg-white/5 backdrop-blur-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.55)] overflow-hidden">
      <img src={src} alt={alt} loading="lazy" className="rounded-xl w-full h-auto object-cover border border-white/10 transition-transform duration-500 ease-out lg:group-hover:scale-110" />
    </div>
  </motion.div>
);

const FeatureRow = ({ eyebrow, title, description, bullets, image, imageAlt, reverse, testid }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center py-14 sm:py-16" data-testid={testid}>
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={reverse ? 'lg:order-2' : 'lg:order-1'}
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {title}
      </h3>
      <p className="text-zinc-400 text-base mb-6">{description}</p>
      <ul className="space-y-2.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-zinc-300 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" /> {b}
          </li>
        ))}
      </ul>
    </motion.div>
    <Shot src={image} alt={imageAlt} reverse={reverse} />
  </div>
);

const StatTile = ({ icon: Icon, value, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-5 flex flex-col justify-center"
  >
    <Icon className="w-5 h-5 text-primary mb-2" />
    <p className="text-xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
  </motion.div>
);

const CompareCell = ({ val, en }) => {
  if (val === true) return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 shadow-[0_0_16px_-2px_rgba(16,185,129,0.6)]">
      <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />
    </span>
  );
  if (val === false) return <span className="inline-block w-1.5 h-1.5 rounded-full bg-zinc-700 mx-auto" />;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold text-primary bg-primary/10 border border-primary/25 whitespace-nowrap">
      {val[en ? 1 : 0]}
    </span>
  );
};

const shuffle = (arr) => arr.map((v) => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map(([, v]) => v);

// Les 10 mouvements les plus "basiques" (connus de tous, un par groupe musculaire clé) —
// figés (pas de tirage aléatoire) pour un rendu marketing propre et jamais 2x le même exercice.
const BASIC_MOVES_PRIORITY = [
  'squat barre', 'développé couché barre', 'soulevé de terre', 'tirage vertical poulie',
  'développé militaire barre', 'curl barre', 'extension poulie haute', 'crunch',
  'hip thrust barre', 'fentes avant', 'pompes classiques', 'traction pronation',
];

const pickBasicMoves = (list) => {
  const norm = (s) => (s || '').toLowerCase();
  const used = new Set();
  const picked = [];
  BASIC_MOVES_PRIORITY.forEach((key) => {
    if (picked.length >= 10) return;
    const match = list.find((e) => !used.has(e.name_fr || e.name) && norm(e.name_fr || e.name).includes(key));
    if (match) { picked.push(match); used.add(match.name_fr || match.name); }
  });
  if (picked.length < 10) {
    list.forEach((e) => {
      if (picked.length >= 10) return;
      const name = e.name_fr || e.name;
      if (!used.has(name)) { picked.push(e); used.add(name); }
    });
  }
  return picked;
};

const SoraVideoCarousel = ({ en }) => {
  const [videos, setVideos] = React.useState(SORA_VIDEOS);
  const [totalReady, setTotalReady] = React.useState(null);

  React.useEffect(() => {
    fetch(`${MEDIA}/api/exercises/public/videos`)
      .then((r) => r.json())
      .then((d) => {
        const list = d.exercises || [];
        if (list.length) {
          setTotalReady(list.length);
          setVideos(pickBasicMoves(list).map((e) => ({
            group_fr: e.muscle_group, group_en: e.muscle_group, url: e.video_url,
          })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="mt-8 -mx-4 sm:mx-0 px-4 sm:px-0" data-testid="sora-video-carousel">
      <div className="mb-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary flex items-center gap-2">
          <Video className="w-3.5 h-3.5" /> {en ? 'The 10 essential moves, Sora 2 demos' : 'Les 10 mouvements essentiels, démos Sora 2'}
          {totalReady != null && <span className="text-zinc-500 font-normal">({totalReady}/449)</span>}
        </p>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none">
        {videos.map((v, i) => (
          <div
            key={i}
            className="relative shrink-0 w-32 sm:w-36 rounded-2xl border border-white/10 bg-black overflow-hidden snap-start shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
            data-testid={`sora-carousel-item-${i}`}
          >
            <video src={`${MEDIA}${v.url}`} autoPlay loop muted playsInline className="w-full aspect-[3/4] object-cover" />
            <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-black/70 text-white backdrop-blur-sm">
              {en ? v.group_en : v.group_fr}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ComparisonTable = ({ en, L }) => {
  const highlightKey = (COACHING_TIERS.find((t) => t.popular) || {}).key || 'solo';
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 border-t border-white/5" data-testid="app-showcase-comparison">
      <div className="max-w-2xl mb-10">
        <Eyebrow>{L('Comparatif détaillé', 'Detailed comparison')}</Eyebrow>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {L('Quelle formule est faite pour vous ?', 'Which plan is right for you?')}
        </h3>
        <p className="text-zinc-400 text-base mt-4">
          {L("Tout ce qui est inclus — ou non — dans chaque formule, en un coup d'œil.", "Everything included — or not — in each plan, at a glance.")}
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative"
      >
        <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-primary/20 via-fuchsia-500/10 to-purple-600/15 blur-2xl opacity-60 -z-10" aria-hidden="true" />
        <div className="overflow-x-auto rounded-[1.75rem] border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.55)]">
          <table className="w-full min-w-[760px] text-sm" data-testid="comparison-table">
            <thead>
              <tr>
                <th className="text-left px-5 py-5 font-semibold text-zinc-400 sticky left-0 bg-zinc-950/90 backdrop-blur-xl z-10 min-w-[220px]">
                  {L("Ce qui est inclus", "What's included")}
                </th>
                {COACHING_TIERS.map((t) => {
                  const isHighlight = t.key === highlightKey;
                  return (
                    <th
                      key={t.key}
                      className={`px-4 py-5 text-center relative min-w-[140px] ${isHighlight ? 'bg-gradient-to-b from-primary/15 via-purple-600/10 to-transparent' : ''}`}
                    >
                      {isHighlight && (
                        <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-white bg-gradient-to-r from-primary to-purple-600 whitespace-nowrap shadow-[0_0_20px_-4px_rgba(217,70,239,0.8)] inline-flex items-center gap-1">
                          <Crown className="w-2.5 h-2.5" /> {L('POPULAIRE', 'POPULAR')}
                        </span>
                      )}
                      <div className={`font-extrabold text-sm sm:text-base mt-3 ${isHighlight ? 'text-white' : 'text-zinc-200'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {t.name[en ? 1 : 0]}
                      </div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-1">{t.price} CHF {t.unit[en ? 1 : 0]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {COACHING_COMPARE.map((f, ri) => (
                <tr key={ri} className="border-t border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="text-left px-5 py-4 text-zinc-300 sticky left-0 bg-zinc-950/80 backdrop-blur-xl font-medium">
                    {f.label[en ? 1 : 0]}
                  </td>
                  {f.v.map((val, ci) => (
                    <td key={ci} className={`px-4 py-4 text-center ${COACHING_TIERS[ci]?.key === highlightKey ? 'bg-primary/[0.04]' : ''}`}>
                      <CompareCell val={val} en={en} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </section>
  );
};

/* ---------------- Page ---------------- */

const AppShowcasePage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const [heroVideo] = React.useState(() => SORA_VIDEOS[0]);
  const promo = useSoloAnnualPromo();
  const tiers = promo?.active
    ? COACHING_TIERS.map((t) => (t.key === 'solo' ? {
        ...t,
        alt: [
          `🔥 -${promo.discountPct}% : ${promo.price}.- / an au lieu de ${promo.originalPrice}.- (jusqu'au 31 oct.) · sans engagement`,
          `🔥 -${promo.discountPct}%: CHF ${promo.price} / yr instead of ${promo.originalPrice} (until Oct 31) · no commitment`,
        ],
      } : t))
    : COACHING_TIERS;

  const scrollToPricing = () => {
    const headerOffset = window.innerWidth >= 640 ? 96 : 64;
    let attempts = 0;
    const tick = () => {
      const el = document.getElementById('tarifs-app');
      if (!el) return;
      const target = el.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
      const distance = Math.abs(window.pageYOffset - target);
      window.scrollTo({ top: target, behavior: attempts === 0 ? 'smooth' : 'auto' });
      attempts += 1;
      // Le contenu au-dessus (images/vidéos en lazy-load) peut grandir pendant le scroll —
      // on ré-évalue la position réelle plusieurs fois pour corriger la cible en conséquence.
      if (distance > 40 && attempts < 15) {
        setTimeout(tick, 80);
      }
    };
    tick();
  };

  return (
    <>
      <SEOHead
        title={L("L'App Body-Up : suivi nutrition, musculation & IA tout-en-un", 'The Body-Up App: all-in-one nutrition, training & AI tracking')}
        description={L('Calculateur de calories, plans alimentaires sur mesure, 180 recettes hyperprotéinées, programmes de musculation ET cardio générés par IA, en manuel (débutant/avancé) ou avec coach, bibliothèque de 449 exercices avec vidéos Sora 2, créateur d\'exercices personnalisés par IA, scan photo de vos repas, suivi complet. Essai gratuit de 7 jours.', 'Calorie calculator, custom meal plans, 180 high-protein recipes, AI-generated training AND cardio programs, manual (beginner/advanced) or coach-built, a 449-exercise library with Sora 2 videos, AI custom exercise creator, AI meal photo scanning, full progress tracking. 7-day free trial.')}
        path="/application"
      />

      <main className="min-h-screen bg-black overflow-hidden">
        {/* ---------- HERO ---------- */}
        <section className="relative pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute -top-24 left-1/4 w-[30rem] h-[30rem] bg-primary/25 rounded-full blur-[130px] opacity-70" />
            <div className="absolute top-1/3 -right-24 w-[28rem] h-[28rem] bg-purple-600/20 rounded-full blur-[130px] opacity-60" />
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          </div>

          <div className="max-w-6xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.span
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 mb-4"
                data-testid="app-showcase-trial-badge"
              >
                🎁 {L('Essai gratuit 7 jours — sans engagement', '7-day free trial — no commitment')}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold text-primary bg-primary/10 border border-primary/30 mb-6 ml-2"
                data-testid="app-showcase-badge"
              >
                <Sparkles className="w-3.5 h-3.5" /> {L("L'application Body-Up", 'The Body-Up app')}
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {L('Le suivi ultra complet,', 'Ultra-complete tracking,')}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-fuchsia-500 to-purple-500">
                  {L('tout-en-un.', 'all-in-one.')}
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="text-lg text-zinc-400 mt-6 max-w-xl"
              >
                {L(
                  'Calculateur de calories, plans alimentaires sur-mesure, 180 recettes hyperprotéinées, programmes de musculation ET cardio générés par IA, en manuel ou avec coach, bibliothèque de 449 exercices ultra-pro, créateur d\'exercices personnalisés par IA, scan photo de vos repas et suivi complet de votre progression — avec votre coach toujours à portée de message.',
                  'Calorie calculator, custom meal plans, 180 high-protein recipes, AI-generated training AND cardio programs, manual or coach-built, a 449-exercise ultra-pro library, AI custom exercise creator, meal photo scanning and full progress tracking — with your coach always one message away.'
                )}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-4 mt-8"
              >
                <Link
                  to="/calculateur-calories"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-purple-600 hover:scale-[1.03] transition-transform shadow-[0_0_30px_rgba(217,70,239,0.3)]"
                  data-testid="hero-cta-try"
                >
                  {L('Essayer 7 jours gratuits', 'Try 7 days free')} <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={scrollToPricing}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-white/5 border-2 border-white/15 hover:border-white/40 transition-colors"
                  data-testid="hero-cta-pricing"
                >
                  {L('Voir les tarifs', 'See pricing')}
                </button>
              </motion.div>
              <div className="flex items-center gap-6 mt-10 text-xs text-zinc-500 flex-wrap">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> {L('Sans engagement', 'No commitment')}</span>
                <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-yellow-400" /> {L('IA Claude, GPT & Sora 2', 'Claude, GPT & Sora 2 AI')}</span>
                <span className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-primary" /> {L('Coach humain inclus', 'Human coach included')}</span>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25, duration: 0.7 }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-primary/30 via-fuchsia-500/20 to-purple-600/25 blur-3xl opacity-70 -z-10" aria-hidden="true" />
              <motion.div
                animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="group rounded-[1.75rem] border border-white/10 bg-white/5 backdrop-blur-xl p-2 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden"
              >
                <img src="https://static.prod-images.emergentagent.com/jobs/7ec0a196-30d6-48cc-b4da-a55574a12eb3/images/205aeb78d302cf76fa60530bc5e1bfb43801e61386acc0ac02ba9554bf82ccb8.jpeg" alt={L("L'application Body-Up sur smartphone", 'The Body-Up app on smartphone')} className="rounded-2xl w-full h-auto border border-white/10 transition-transform duration-500 ease-out lg:group-hover:scale-110" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 }}
                className="absolute -left-6 bottom-10 hidden sm:flex items-center gap-2 px-3.5 py-2.5 rounded-2xl bg-zinc-900/95 border border-white/10 shadow-xl backdrop-blur-xl"
              >
                <Camera className="w-4 h-4 text-primary" />
                <span className="text-xs font-bold text-white">{L('Scan IA en 3s', 'AI scan in 3s')}</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 }}
                className="absolute -right-4 top-8 hidden sm:flex items-center gap-2 px-3.5 py-2.5 rounded-2xl bg-zinc-900/95 border border-white/10 shadow-xl backdrop-blur-xl"
              >
                <Dumbbell className="w-4 h-4 text-primary" />
                <span className="text-xs font-bold text-white">449 {L('exercices', 'exercises')}</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 1.3, duration: 0.6 }}
                className="absolute -left-10 bottom-28 hidden md:block w-32 sm:w-36 rounded-2xl border border-primary/40 bg-black shadow-[0_20px_50px_rgba(0,0,0,0.7)] overflow-hidden"
                data-testid="hero-sora-video-card"
              >
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-primary/40 to-fuchsia-600/30 blur-xl opacity-70 -z-10" aria-hidden="true" />
                <video
                  key={heroVideo.url}
                  src={`${MEDIA}${heroVideo.url}`}
                  autoPlay loop muted playsInline
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="hero-sora-video"
                />
                <span className="absolute top-1.5 left-1.5">
                  <img src="/icon-192.png" alt="Body-Up" className="w-7 h-7 rounded-full ring-1 ring-white/60 logo-soft-pulse" />
                </span>
                <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold bg-black/70 text-white backdrop-blur-sm">
                  {en ? heroVideo.group_en : heroVideo.group_fr}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ---------- FEATURES ---------- */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-white/5">
          <FeatureRow
            testid="feature-calculator"
            eyebrow={L('Calculateur scientifique', 'Scientific calculator')}
            title={L('Le calculateur de calories le plus complet', 'The most complete calorie calculator')}
            description={L(
              "Basé sur des formules scientifiques éprouvées, il s'adapte à votre morphotype, votre objectif, votre niveau d'activité et même vos spécificités hormonales (périménopause, ménopause) ou votre jeûne intermittent.",
              'Built on proven scientific formulas, it adapts to your body type, goal, activity level, and even hormonal specifics (peri/menopause) or intermittent fasting.'
            )}
            bullets={[
              L('Calcul précis des calories & macronutriments', 'Precise calories & macros calculation'),
              L('Version femme ultra-avancée (hormones, cycle)', 'Advanced women-specific version (hormones, cycle)'),
              L('Résultats détaillés + conseils personnalisés (version Pro)', 'Detailed results + personalized advice (Pro version)'),
              L('100% gratuit pour démarrer', '100% free to get started'),
            ]}
            image={`${IMG}/calorie-calculator.jpg`}
            imageAlt={L('Calculateur de calories Body-Up', 'Body-Up calorie calculator')}
          />

          <FeatureRow
            testid="feature-photo-scan"
            reverse
            eyebrow={L('Intelligence artificielle', 'Artificial intelligence')}
            title={L('Photographiez votre assiette, l\'IA fait le reste', 'Snap a photo of your plate, AI does the rest')}
            description={L(
              "Plus besoin de peser ou de chercher chaque aliment : prenez simplement une photo de votre repas, notre IA reconnaît les aliments et estime instantanément calories, protéines, glucides et lipides.",
              'No more weighing or searching for every food item: just snap a photo of your meal, our AI recognizes the food and instantly estimates calories, protein, carbs and fat.'
            )}
            bullets={[
              L('Reconnaissance automatique du repas par photo', 'Automatic meal recognition from a photo'),
              L('Estimation instantanée des macros', 'Instant macro estimation'),
              L('Recherche IA illimitée dans la base alimentaire', 'Unlimited AI search in the food database'),
              L('Historique de journal illimité', 'Unlimited journal history'),
            ]}
            image={`${IMG}/ai-photo-scan.jpg`}
            imageAlt={L('Scan IA d\'un repas', 'AI meal scan')}
          />

          <FeatureRow
            testid="feature-macro-tracking"
            eyebrow={L('Suivi quotidien', 'Daily tracking')}
            title={L('Vos macros & calories suivies au quotidien', 'Your macros & calories tracked every day')}
            description={L(
              "Anneaux de progression, journal de repas, hydratation, humeur, activité : toute votre journée nutritionnelle visualisée en un coup d'œil, avec des rappels bienveillants pour rester régulier.",
              'Progress rings, meal journal, hydration, mood, activity: your whole nutritional day visualized at a glance, with friendly reminders to stay consistent.'
            )}
            bullets={[
              L('Anneaux calories & macros en temps réel', 'Real-time calories & macros rings'),
              L('Journal de repas illimité avec photos', 'Unlimited meal journal with photos'),
              L('Suivi hydratation, humeur & activité', 'Hydration, mood & activity tracking'),
              L('Défis, streaks & badges de motivation', 'Challenges, streaks & motivation badges'),
            ]}
            image={`${IMG}/progress-charts.jpg`}
            imageAlt={L('Graphiques de suivi nutrition', 'Nutrition tracking charts')}
          />

          <FeatureRow
            testid="feature-meal-plan"
            reverse
            eyebrow={L('Nutrition personnalisée', 'Personalized nutrition')}
            title={L('Des plans alimentaires 100% sur mesure', 'Meal plans that are 100% custom')}
            description={L(
              "Votre coach vous construit un plan alimentaire personnalisé, adapté à vos allergies, préférences et horaires d'entraînement — renouvelé chaque mois pour suivre votre évolution.",
              'Your coach builds you a personalized meal plan, adapted to your allergies, preferences and training schedule — refreshed every month to follow your progress.'
            )}
            bullets={[
              L('Plan ultra personnalisé par votre coach', "Ultra-personalized plan by your coach"),
              L('Adapté allergies, préférences & nb de repas', 'Adapted to allergies, preferences & meals/day'),
              L("Aligné sur vos horaires d'entraînement", 'Aligned with your training schedule'),
              L('Renouvelé chaque mois selon vos progrès', 'Refreshed every month based on your progress'),
            ]}
            image={`${IMG}/meal-plan.jpg`}
            imageAlt={L('Plan alimentaire personnalisé', 'Personalized meal plan')}
          />

          <FeatureRow
            testid="feature-workout-program"
            eyebrow={L('Musculation & cardio sur mesure', 'Custom strength & cardio training')}
            title={L('Des programmes muscu ET cardio qui vous ressemblent', 'Strength & cardio programs that fit you')}
            description={L(
              "Séances structurées, séries, charges, tempo, temps de repos : chaque programme est conçu pour votre niveau et vos objectifs, avec un suivi de progression tonnage à chaque séance.",
              'Structured sessions, sets, loads, tempo, rest times: every program is designed for your level and goals, with tonnage progress tracking at every session.'
            )}
            bullets={[
              L('Musculation ET cardio, dans la même bibliothèque', 'Strength AND cardio, in the same library'),
              L('4 façons de créer : IA, manuel débutant, manuel avancé, ou par votre coach', '4 ways to build it: AI, manual beginner, manual advanced, or by your coach'),
              L('Suivi de charge, séries & tempo en temps réel', 'Real-time load, sets & tempo tracking'),
              L('Minuteur de repos intégré pendant la séance', 'Built-in rest timer during the session'),
            ]}
            image={`${IMG}/workout-program.jpg`}
            imageAlt={L('Programme de musculation personnalisé', 'Personalized strength program')}
          />

          <FeatureRow
            testid="feature-ai-generator"
            reverse
            eyebrow={L('Création par IA', 'AI creation')}
            title={L('Générez votre programme avec l\'intelligence artificielle', 'Generate your program with AI')}
            description={L(
              "En quelques secondes, notre IA construit un programme complet et cohérent à partir de votre niveau, votre matériel disponible et vos objectifs — puisé directement dans notre bibliothèque d'exercices ultra-pro.",
              'In seconds, our AI builds a complete, coherent program based on your level, available equipment and goals — drawing directly from our ultra-pro exercise library.'
            )}
            bullets={[
              L('Génération instantanée par IA (Claude)', 'Instant AI generation (Claude)'),
              L('100% cohérent avec la bibliothèque d\'exercices', "100% consistent with the exercise library"),
              L("Invente et illustre même de nouveaux exercices si besoin", 'Even invents and illustrates new exercises if needed'),
              L('Modifiable à tout moment avec votre coach', 'Editable anytime with your coach'),
            ]}
            image={`${IMG}/ai-workout-generator.jpg`}
            imageAlt={L('Générateur de programme par IA', 'AI workout program generator')}
          />
        </section>

        {/* ---------- BENTO : BIBLIOTHÈQUE D'EXERCICES ---------- */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 border-t border-white/5">
          <div className="max-w-2xl mb-10">
            <Eyebrow>{L('La référence en exercices', 'The exercise reference')}</Eyebrow>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Une bibliothèque d\'exercices ultra-pro', 'An ultra-pro exercise library')}
            </h3>
            <p className="text-zinc-400 text-base mt-4">
              {L(
                "449 exercices référencés, chacun illustré par IA et démontré en vidéo animée (Sora 2) — pour ne jamais douter de la bonne exécution d'un mouvement.",
                '449 referenced exercises, each illustrated by AI and demonstrated with an animated video (Sora 2) — so you never doubt proper form again.'
              )}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group lg:col-span-2 lg:row-span-2 relative rounded-3xl overflow-hidden border border-white/10 min-h-[280px]"
            >
              <img src={`${IMG}/exercise-library.jpg`} alt={L("Bibliothèque d'exercices Body-Up", 'Body-Up exercise library')} className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 ease-out lg:group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </motion.div>
            <StatTile icon={Dumbbell} value="449" label={L('exercices référencés', 'referenced exercises')} />
            <StatTile icon={Video} value="Sora 2" label={L('vidéos de démonstration animées', 'animated demo videos')} />
            <StatTile icon={Sparkles} value="IA" label={L('images générées automatiquement', 'automatically generated images')} />
            <StatTile icon={Award} value={L('Favoris', 'Favorites')} label={L('notez et retrouvez vos exercices préférés', 'rate and find your favorite exercises')} />
          </div>
          <SoraVideoCarousel en={en} />
        </section>

        {/* ---------- SUITE FEATURES ---------- */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-white/5">
          <FeatureRow
            testid="feature-custom-exercise-creator"
            eyebrow={L('Créez vos propres exercices', 'Create your own exercises')}
            title={L('Un exercice manque ? Créez-le vous-même, avec l\'IA', 'Missing an exercise? Create it yourself, with AI')}
            description={L(
              "Depuis « Ma bibliothèque créée », décrivez simplement le nom de votre exercice : l'IA rédige l'exécution gratuitement, puis génère une image ou une vraie vidéo animée (Sora 2) pour l'illustrer.",
              'From "My created library", simply describe your exercise name: AI writes the execution text for free, then generates an image or a real animated video (Sora 2) to illustrate it.'
            )}
            bullets={[
              L('Texte d\'exécution généré par IA, toujours gratuit', 'AI-generated execution text, always free'),
              L('Image IA (-2 crédits) ou vraie vidéo Sora 2 (-5 crédits)', 'AI image (-2 credits) or real Sora 2 video (-5 credits)'),
              L('5 crédits média inclus chaque mois dès Suivi Pro', '5 media credits included every month from Suivi Pro'),
              L('Vos exercices persos réutilisables dans vos programmes', 'Your custom exercises reusable in your programs'),
            ]}
            image={`${IMG}/ai-workout-generator.jpg`}
            imageAlt={L('Créateur d\'exercices personnalisés par IA', 'AI custom exercise creator')}
          />

          <FeatureRow
            testid="feature-recipes"
            reverse
            eyebrow={L('180 recettes prêtes à cuisiner', '180 ready-to-cook recipes')}
            title={L('Une bibliothèque de 180 recettes hyperprotéinées', 'A library of 180 high-protein recipes')}
            description={L(
              "Petit-déjeuner, déjeuner, dîner, collations : piochez dans 180 recettes hyperprotéinées avec macros calculées, ajoutez-les à votre journal en un tap, ou gardez vos favorites dans votre bibliothèque perso.",
              'Breakfast, lunch, dinner, snacks: pick from 180 high-protein recipes with calculated macros, log them to your journal in one tap, or keep your favorites in your personal library.'
            )}
            bullets={[
              L('180 recettes hyperprotéinées, macros incluses', '180 high-protein recipes, macros included'),
              L('Recette du jour, thème de la semaine & top du mois', 'Recipe of the day, weekly theme & monthly top'),
              L('Favoris, notes perso & export PDF', 'Favorites, personal notes & PDF export'),
              L('Incluses dès Suivi Pro (9.90 CHF/mois)', 'Included from Suivi Pro (9.90 CHF/month)'),
            ]}
            image={`${IMG}/meal-plan.jpg`}
            imageAlt={L('Bibliothèque de recettes hyperprotéinées', 'High-protein recipe library')}
          />

          <FeatureRow
            testid="feature-workout-stats"
            eyebrow={L('Records & progression', 'Records & progress')}
            title={L('Suivez votre force séance après séance', 'Track your strength session after session')}
            description={L(
              "Tonnage total, records personnels (1RM, 6RM, 10RM, 15RM), badges de performance : visualisez concrètement votre progression en musculation, semaine après semaine.",
              'Total tonnage, personal records (1RM, 6RM, 10RM, 15RM), performance badges: concretely visualize your strength progress, week after week.'
            )}
            bullets={[
              L('Records personnels calculés automatiquement', 'Personal records calculated automatically'),
              L('Graphiques de tonnage & volume d\'entraînement', 'Tonnage & training volume charts'),
              L('Badges de performance débloquables', 'Unlockable performance badges'),
              L('Historique complet de toutes vos séances', 'Full history of every session'),
            ]}
            image={`${IMG}/workout-stats.jpg`}
            imageAlt={L('Statistiques de musculation', 'Strength training stats')}
          />

          <FeatureRow
            testid="feature-coach"
            reverse
            eyebrow={L('Un vrai humain, pas un bot', 'A real human, not a bot')}
            title={L('Votre coach, à portée de message', 'Your coach, one message away')}
            description={L(
              "Derrière l'IA, il y a toujours Laurent : messagerie directe avec votre coach, ajustements de votre programme, réponses à vos questions — un vrai accompagnement humain.",
              "Behind the AI, there's always Laurent: direct messaging with your coach, program adjustments, answers to your questions — real human support."
            )}
            bullets={[
              L('Messagerie directe avec votre coach dans l\'app', 'Direct messaging with your coach in the app'),
              L('Ajustements réguliers de votre programme', 'Regular adjustments to your program'),
              L('Bilans mensuels avec photos comparatives', 'Monthly reviews with comparison photos'),
              L('Notifications quand un exercice ne convient pas', "Alerts when an exercise isn't working for you"),
            ]}
            image={`${IMG}/coach-messaging.jpg`}
            imageAlt={L('Messagerie avec le coach', 'Coach messaging')}
          />
        </section>

        {/* ---------- COMPARATIF DÉTAILLÉ ---------- */}
        <ComparisonTable en={en} L={L} />

        {/* ---------- PRICING ---------- */}
        <section id="tarifs-app" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 border-t border-white/5">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow>{L('Tarifs', 'Pricing')}</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Un plan pour chaque niveau d\'accompagnement', 'A plan for every level of support')}
            </h2>
            <p className="text-zinc-400 text-base mt-4">
              {L('De l\'app seule au coaching hybride en studio, choisissez le niveau d\'accompagnement qui vous correspond.', 'From the app alone to hybrid in-studio coaching, choose the level of support that fits you.')}
            </p>
            <p className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full text-sm font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30">
              🎁 {L('Suivi Pro et Suivi Pro+ : 7 jours gratuits, sans engagement', 'Suivi Pro & Suivi Pro+: 7 days free, no commitment')}
            </p>
          </div>
          <SoloAnnualPromoCard promo={promo} language={language} ctaHref="/checkout/solo" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="app-showcase-pricing-grid">
            {tiers.map((t, i) => <OfferCard key={t.key} t={t} en={en} L={L} index={i} />)}
          </div>
          <p className="text-center text-xs text-zinc-500 mt-10 inline-flex items-center justify-center gap-2 w-full">
            <ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Paiement sécurisé Stripe · Abonnements flexibles, résiliables à tout moment', 'Secure Stripe payment · Flexible subscriptions, cancel anytime')}
          </p>
        </section>

        {/* ---------- CTA FINAL ---------- */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="relative rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-purple-600/10 to-black p-10 sm:p-14 text-center overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-primary/20 rounded-full blur-[100px]" aria-hidden="true" />
            <Flame className="w-8 h-8 text-primary mx-auto mb-4" />
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Prêt à démarrer votre transformation ?', 'Ready to start your transformation?')}
            </h3>
            <p className="text-zinc-400 mt-3 max-w-lg mx-auto">
              {L("Commencez gratuitement avec le calculateur, puis débloquez tout l'écosystème Body-Up quand vous êtes prêt.", 'Start for free with the calculator, then unlock the full Body-Up ecosystem when you\'re ready.')}
            </p>
            <Link
              to="/calculateur-calories"
              className="inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-purple-600 hover:scale-[1.03] transition-transform"
              data-testid="final-cta-try"
            >
              {L('Essayer gratuitement', 'Try it for free')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default AppShowcasePage;
